'use client';

import React, { useContext } from 'react';
import type { ElementType, ReactNode } from 'react';
import { cx } from '@milanote/roma/utils/cx';
import type { CommandMenuItemStateProps, CommandMenuSize } from './command-menu.types';
import { CommandMenuContext } from './command-menu-context';
import { styles } from './command-menu-item-styles';
import type { PolymorphicProps } from '../../../types/polymorphic';

const COMMAND_MENU_ITEM_DISPLAY_NAME = 'RomaCommandMenuItem';

type CommandMenuItemRootOwnProps = CommandMenuItemStateProps & {
    size?: CommandMenuSize;
    className?: string;
    hasDescription?: boolean;
    hasLeading?: boolean;
    children: ReactNode;
};

type CommandMenuItemRootComponentProps<T extends ElementType> = PolymorphicProps<CommandMenuItemRootOwnProps, T>;

export type CommandMenuItemRootBaseProps = PolymorphicProps<CommandMenuItemRootOwnProps>;

/**
 * Renders the shared outer shell for command menu items across structured and custom variants.
 */
export const CommandMenuItemBase = <T extends ElementType = 'div'>(props: CommandMenuItemRootComponentProps<T>) => {
    const {
        as,
        size: sizeProp,
        className,
        hasDescription,
        hasLeading,
        isSelected,
        hasSubmenu,
        isOpen,
        isDisabled,
        isFocused,
        isFocusVisible,
        isHovered,
        children,
        ...restProps
    } = props;

    const { size: contextSize } = useContext(CommandMenuContext);
    const size = sizeProp ?? contextSize;
    const sizeStyle = styles.sizes[size];
    const Component = as ?? 'div';

    return (
        <Component
            {...restProps}
            data-size={size}
            data-selected={isSelected || undefined}
            data-has-submenu={hasSubmenu || undefined}
            data-open={isOpen && !isDisabled || undefined}
            data-has-description={hasDescription || undefined}
            data-has-leading={hasLeading || undefined}
            data-disabled={isDisabled || undefined}
            data-focused={isFocused && !isDisabled || undefined}
            data-focus-visible={isFocusVisible && !isDisabled || undefined}
            data-hovered={isHovered && !isDisabled || undefined}
            className={cx(
                COMMAND_MENU_ITEM_DISPLAY_NAME,
                styles.common.shell,
                sizeStyle.shell,
                className,
            )}
        >
            {children}
        </Component>
    );
};

CommandMenuItemBase.displayName = COMMAND_MENU_ITEM_DISPLAY_NAME;
